import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Hotel, createHotel, updateHotel, getHotelDetails, getHotelDetailsPublic } from '../../api/hotels.ts';
import { toastError, toastSuccess } from '../../shared/ui/toast.ts';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import HotelMediaTab from './HotelMediaTab.tsx';
import RoomTypesTab from './RoomTypesTab.tsx';
import AvailabilityTab from './AvailabilityTab.tsx';
import PromosExtrasTab from './PromosExtrasTab.tsx';
import { useTenant } from '../../components/ContextTenant.tsx';

const empty: Partial<Hotel> = { name: '', city: '', stars: 3 };
const HOTEL_TAB_KEYS = ['media', 'rooms', 'availability', 'promos'] as const;

const hotelTabFromQuery = (value: string | null) => {
  const index = HOTEL_TAB_KEYS.indexOf(value as (typeof HOTEL_TAB_KEYS)[number]);
  return index >= 0 ? index : 0;
};

const HotelForm: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = hotelTabFromQuery(searchParams.get('tab'));
  const isNew = id === 'new';
  const [model, setModel] = useState<Partial<Hotel>>(empty);
  const nav = useNavigate();
  const { tenantId } = useTenant();

  useEffect(() => {
    if (!isNew && id) {
      Promise.allSettled([
        getHotelDetails(Number(id)),
        getHotelDetailsPublic(Number(id)),
      ]).then(([adminResult, publicResult]) => {
        const adminHotel = adminResult.status === 'fulfilled' ? adminResult.value : null;
        const publicPayload = publicResult.status === 'fulfilled' ? publicResult.value : null;
        const publicHotel = publicPayload?.hotel || publicPayload || null;
        setModel({ ...empty, ...(publicHotel || {}), ...(adminHotel || {}) });
      }).catch((e) => {
        toastError('Failed to load hotel');
        setModel({ ...empty });
      });
    }
  }, [id, isNew]);

  const onChange = (k: keyof Hotel) => (e: any) => setModel({ ...model, [k]: e.target.value });

  const onTabChange = (_: React.SyntheticEvent, nextTab: number) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('tab', HOTEL_TAB_KEYS[nextTab] || HOTEL_TAB_KEYS[0]);
    setSearchParams(nextSearchParams);
  };

  const onSubmit = async () => {
    try {
      if (isNew) {
        if (!tenantId || tenantId <= 0) {
          toastError('Select a tenant before creating a hotel');
          return;
        }
        const created = await createHotel({ name: model.name!, city: model.city!, address: model.address, stars: model.stars || 3, amenities: model.amenities, tenantId });
        toastSuccess('Hotel created');
        nav(`/dashboard/hotels/${created.id}`);
      } else {
        const updated = await updateHotel(Number(id), { ...model });
        setModel(updated);
        toastSuccess('Saved');
      }
    } catch (e:any) {
      const msg = e?.response?.data?.error || 'Save failed';
      toastError(msg);
    }
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">{isNew ? 'Create Hotel' : `Edit Hotel #${id}`}</Typography>
        <Button variant="contained" onClick={onSubmit} disabled={isNew && (!tenantId || tenantId <= 0)}>{isNew ? 'Create' : 'Save'}</Button>
      </Stack>
      {isNew && (!tenantId || tenantId <= 0) && (
        <Typography color="warning.main" role="alert" sx={{ mb: 2 }}>
          Select a tenant before creating a hotel.
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField fullWidth label="Name" value={model.name||''} onChange={onChange('name')} sx={{ mb:2 }} />
          <Stack direction={{ xs:'column', md:'row' }} spacing={2}>
            <TextField fullWidth label="City" value={model.city||''} onChange={onChange('city')} />
            <TextField fullWidth label="Address" value={model.address||''} onChange={onChange('address')} />
            <TextField fullWidth type="number" inputProps={{ min:1, max:5 }} label="Stars" value={model.stars||3} onChange={onChange('stars')} />
          </Stack>
          <TextField fullWidth multiline minRows={3} label="Amenities" value={model.amenities||''} onChange={onChange('amenities')} sx={{ mt:2 }} />
          <Stack direction={{ xs:'column', md:'row' }} spacing={2} sx={{ mt:2 }}>
            <TextField fullWidth label="Cover URL" value={model.coverImageUrl||''} InputProps={{ readOnly:true }} />
            <TextField fullWidth label="Brochure URL" value={model.brochurePdfUrl||''} InputProps={{ readOnly:true }} />
          </Stack>
        </Grid>
      </Grid>

      {!isNew && (
        <Box mt={4}>
          <Tabs value={tab} onChange={onTabChange}>
            <Tab label="Media" />
            <Tab label="Room Types" />
            <Tab label="Availability" />
            <Tab label="Promos/Extras" />
          </Tabs>
          {tab===0 && <HotelMediaTab hotelId={Number(id)} model={model} setModel={setModel} />}
          {tab===1 && <RoomTypesTab hotelId={Number(id)} />}
          {tab===2 && <AvailabilityTab hotelId={Number(id)} />}
          {tab===3 && <PromosExtrasTab hotelId={Number(id)} />}
        </Box>
      )}
    </Box>
  );
};

export default HotelForm;


